import { RequestStatusBadge } from "@/components/common/section-components/badge/RequestStatusBadge";
import { SuppliesOrderStatus } from "@/constants/entity-status";
import { AppColors } from "@/styles/colors";
import { Button, Flex, Grid, GridItem, Text } from "@chakra-ui/react";
import { Check, PenLine } from "lucide-react";
import { useMemo } from "react";

export interface FertSlotRequestOrdersTableItem {
  id: number;
  nroPedido?: string;
  cantidadToneladas?: number;
  destino?: string;
  lugarDespacho?: string;
  nombreDespacho?: string;
  estado?: string;
  toneladasTotales?: number;
}

interface Props {
  orders: FertSlotRequestOrdersTableItem[];
  loadingConfirmOrderId: number | null;
  onConfirmOrder: (orderId: number) => void;
  showEditOrders?: boolean;
  onEditOrders?: () => void;
}

interface ConfirmOrderButtonProps {
  id: number;
  loadingConfirmOrderId: number | null;
  onConfirmOrder: (orderId: number) => void;
}

const ConfirmOrderButton = ({
  id,
  loadingConfirmOrderId,
  onConfirmOrder,
}: ConfirmOrderButtonProps) => {
  return (
    <Button
      height="28px"
      bgColor={AppColors.GREEN_PRIMARY}
      onClick={() => onConfirmOrder(id)}
      loading={loadingConfirmOrderId === id}
      aria-label="Confirmar pedido"
      padding="8px 12px"
    >
      <Check size={12} />
      <Text fontWeight={600} fontSize="14px">
        Confirmar pedido
      </Text>
    </Button>
  );
};

interface OrderActionElementsProps {
  item: any;
  loadingConfirmOrderId: number | null;
  onConfirmOrder: (orderId: number) => void;
}

const OrderActionElements = ({
  item,
  loadingConfirmOrderId,
  onConfirmOrder,
}: OrderActionElementsProps) => {
  if (item?.estado === SuppliesOrderStatus.PENDING) {
    return (
      <ConfirmOrderButton
        id={Number(item?.id)}
        loadingConfirmOrderId={loadingConfirmOrderId}
        onConfirmOrder={onConfirmOrder}
      />
    );
  }

  return null;
};

export const FertSlotRequestOrdersTable = ({
  orders,
  loadingConfirmOrderId,
  onConfirmOrder,
  showEditOrders,
  onEditOrders,
}: Props) => {
  const items = useMemo(
    () =>
      orders?.map((o) => {
        const nroPedido = o?.nroPedido || `Pedido ${o?.id}`;
        const toneladas = `${new Intl.NumberFormat("es-AR").format(
          o?.toneladasTotales ?? 0,
        )} toneladas`;
        const despacho = o?.nombreDespacho || o?.destino || "Sin despacho";
        const status = o?.estado as unknown as SuppliesOrderStatus;

        return {
          ...o,
          nroPedido,
          toneladas,
          despacho,
          status,
        };
      }) ?? [],
    [orders],
  );

  return (
    <Flex flexDir="column" gap="8px" paddingBottom="12px">
      <Flex alignItems="center" justifyContent="space-between">
        <Text fontWeight={700}>Pedidos</Text>

        {showEditOrders && (
          <Button
            height="28px"
            onClick={onEditOrders}
            padding="8px 12px"
          >
            <PenLine size={12} />
            <Text fontWeight={600} fontSize="14px">
              Editar pedidos
            </Text>
          </Button>
        )}
      </Flex>

      <Flex
        flexDir="column"
        border={`1px solid ${AppColors.WHITE_BORDER_1}`}
        borderRadius="8px"
        overflow="hidden"
        maxH="320px"
        overflowY="auto"
      >
        {items.map((item, index) => (
          <Flex
            key={item?.id ?? index}
            alignItems="center"
            justifyContent="flex-start"
            paddingX="20px"
            paddingY="16px"
            borderTop={index === 0 ? "none" : `1px solid ${AppColors.WHITE_BORDER_1}`}
          >
            <Grid
              templateColumns="140px 150px 150px 140px"
              alignItems="center"
              width="auto"
            >
              <GridItem borderRight={`1px solid ${AppColors.WHITE_BORDER_1}`} paddingRight="24px">
                <Text fontWeight={700} fontSize="14px">
                  {item?.nroPedido}
                </Text>
              </GridItem>

              <GridItem borderRight={`1px solid ${AppColors.WHITE_BORDER_1}`} paddingX="24px">
                <Text fontWeight={600} fontSize="14px" color={AppColors.GRAY_TEXT}>
                  {item?.toneladas}
                </Text>
              </GridItem>

              <GridItem borderRight={`1px solid ${AppColors.WHITE_BORDER_1}`} paddingX="24px">
                <Text
                  fontWeight={600}
                  fontSize="14px"
                  color={AppColors.GRAY_TEXT}
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                >
                  {item?.despacho}
                </Text>
              </GridItem>

              <GridItem paddingLeft="24px">
                <RequestStatusBadge status={item?.status} />
              </GridItem>
            </Grid>

            <Flex flex="1" />

            <OrderActionElements
              item={item}
              loadingConfirmOrderId={loadingConfirmOrderId}
              onConfirmOrder={onConfirmOrder}
            />
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
};
